import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import styles from './NewOrderModal.module.css';
import orderService from '../../services/orderService';
import { clientService } from '../../services/clientService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { getOrderStatus } from '../../types/orders.types';

/**
 * New Order Modal Component
 * 
 * Streamlined workflow for creating orders:
 * 1. Select client
 * 2. Generate order (with auto-ID from backend)
 * 3. Return order to user for next steps
 * 
 * Optimized for speed in multi-user environments
 */
const NewOrderModal = ({ isOpen, onClose, onOrderCreated }) => {
    const { getBackendAccessToken } = useAccessToken();
    
    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // State
    const [step, setStep] = useState(1); // 1: Select Client, 2: Success
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [createdOrder, setCreatedOrder] = useState(null);

    /**
     * Load active clients when modal opens
     */
    useEffect(() => {
        if (isOpen) {
            loadClients();
            // Reset state
            setStep(1);
            setSelectedClient(null);
            setSearchTerm('');
            setError(null);
            setCreatedOrder(null);
        }
    }, [isOpen]);

    /**
     * Load active clients from API
     */
    const loadClients = async () => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const data = await clientService.getActive(accessToken);
            setClients(data);
        } catch (err) {
            console.error('Failed to load clients:', err);
            setError('Failed to load clients. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Filter clients by search term
     */
    const filteredClients = clients.filter(client => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            client.clientName?.toLowerCase().includes(search) ||
            client.clientCode?.toLowerCase().includes(search)
        );
    });

    /**
     * Handle client selection
     */
    const handleClientSelect = (client) => {
        setSelectedClient(client);
    };

    /**
     * Create order for selected client
     * OPTIMIZED: Backend generates ID atomically for multi-user safety
     */
    const handleCreateOrder = async () => {
        if (!selectedClient) return;

        try {
            setCreating(true);
            setError(null);

            const accessToken = await getBackendAccessToken();
            
            // Get today's date in ISO format (YYYY-MM-DD)
            const orderDate = new Date().toISOString().split('T')[0];

            // Create order with minimal data - backend handles ID generation
            // Boolean flags (processed, completed, reviewFlag) default to false on backend
            const orderData = {
                orderDate,
                clientId: selectedClient.clientId
            };

            // Backend generates the order ID atomically (thread-safe for multi-user)
            const order = await orderService.createOrder(accessToken, orderData);
            
            setCreatedOrder(order);
            setStep(2); // Move to success step
        } catch (err) {
            console.error('Failed to create order:', err);
            setError(err.message || 'Failed to create order. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        if (createdOrder && onOrderCreated) {
            // Pass created order to parent
            onOrderCreated(createdOrder);
        }
        onClose();
    };

    /**
     * Handle "Continue" after order creation
     */
    const handleContinue = () => {
        if (createdOrder && onOrderCreated) {
            onOrderCreated(createdOrder);
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <h2>
                        <FaPlus className={styles.headerIcon} />
                        Create New Order
                    </h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label="Close modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.modalBody}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    {/* Step 1: Select Client */}
                    {step === 1 && (
                        <div className={styles.stepContent}>
                            <h3>Select Client</h3>
                            <p className={styles.stepDescription}>
                                Choose the client for this order
                            </p>

                            {/* Search */}
                            <div className={styles.searchBox}>
                                <input
                                    type="text"
                                    placeholder="Search clients by name or code..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className={styles.searchInput}
                                    autoFocus
                                />
                            </div>

                            {/* Client List */}
                            {loading ? (
                                <div className={styles.loadingContainer}>
                                    <FaSpinner className={styles.spinner} />
                                    <span>Loading clients...</span>
                                </div>
                            ) : (
                                <div className={styles.clientList}>
                                    {filteredClients.length === 0 ? (
                                        <div className={styles.noResults}>
                                            {searchTerm 
                                                ? `No clients found matching "${searchTerm}"`
                                                : 'No active clients available'
                                            }
                                        </div>
                                    ) : (
                                        filteredClients.map(client => (
                                            <div
                                                key={client.clientId}
                                                className={`${styles.clientItem} ${
                                                    selectedClient?.clientId === client.clientId 
                                                        ? styles.selected 
                                                        : ''
                                                }`}
                                                onClick={() => handleClientSelect(client)}
                                            >
                                                <div className={styles.clientInfo}>
                                                    <span className={styles.clientCode}>
                                                        {client.clientCode}
                                                    </span>
                                                    <span className={styles.clientName}>
                                                        {client.clientName}
                                                    </span>
                                                </div>
                                                {selectedClient?.clientId === client.clientId && (
                                                    <FaCheckCircle className={styles.selectedIcon} />
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Success */}
                    {step === 2 && createdOrder && (
                        <div className={styles.stepContent}>
                            <div className={styles.successContainer}>
                                <FaCheckCircle className={styles.successIcon} />
                                <h3>Order Created Successfully!</h3>
                                
                                <div className={styles.orderDetails}>
                                    <div className={styles.orderDetailRow}>
                                        <span className={styles.label}>Order ID:</span>
                                        <span className={styles.value}>
                                            {createdOrder.orderId}
                                        </span>
                                    </div>
                                    <div className={styles.orderDetailRow}>
                                        <span className={styles.label}>Client:</span>
                                        <span className={styles.value}>
                                            {createdOrder.clientName} ({createdOrder.clientCode})
                                        </span>
                                    </div>
                                    <div className={styles.orderDetailRow}>
                                        <span className={styles.label}>Date:</span>
                                        <span className={styles.value}>
                                            {new Date(createdOrder.orderDate).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className={styles.orderDetailRow}>
                                        <span className={styles.label}>Status:</span>
                                        <span className={styles.value}>
                                            {getOrderStatus(createdOrder)}
                                        </span>
                                    </div>
                                </div>

                                <p className={styles.successMessage}>
                                    You can now proceed with adding samples and processing this order.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    {step === 1 && (
                        <>
                            <button 
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={creating}
                            >
                                Cancel
                            </button>
                            <button 
                                className={styles.createButton}
                                onClick={handleCreateOrder}
                                disabled={!selectedClient || creating}
                            >
                                {creating ? (
                                    <>
                                        <FaSpinner className={styles.buttonSpinner} />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Order'
                                )}
                            </button>
                        </>
                    )}

                    {step === 2 && (
                        <button 
                            className={styles.continueButton}
                            onClick={handleContinue}
                        >
                            Continue
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewOrderModal;
